import {
  getSubscriptionTypes,
  getSubscriptionsType,
  getToken,
  getUserId,
  getUserRole,
} from "../../utilities/authHelper";
import ProductDashboard from "../ProductDashboard/ProductDashboard";
import AdminPanel from "../AdminPanel/AdminPanel";
import Home from "../Home/Home";
import CoTraderCombined from "../CoTrader/CoTraderCombined";
import TradingEconomicsCalendar from "../Calendar/TradingEconomicsCalendar";
import CoTraderCombinedMobile from "../CoTrader/CoTraderCombinedMobile";
import ProductDashboardMobile from "../ProductDashboard/ProductDashboardMobile";
import HomeMobile from "../Home/HomeMobile";
import TradingEconomicsCalendarMobile from "../Calendar/TradingEconomicsCalendarMobile";
import CoHedgerSummaryDashboardTile from "../CoHedger/CoHedgerSummaryDashboardTile";
import TradeAnalysis from "../TradeAnalysis/TradeAnalysis";
import { decorateDashboardRouteMeta } from "../../utilities/dashboardRouteUtils";
import ProductDashboardNoSubscription from "../ProductDashboard/ProductDashboardNoSubscription";
const isMobile = window.innerWidth < 768;

const subscriptionTypeContains = (subscriptionType, keyword) =>
  String(subscriptionType || "")
    .toLowerCase()
    .includes(String(keyword || "").toLowerCase());

const buildAgentRouteTab = (config) => ({
  ...config,
  subTabs: [],
  hidden: true,
  agentSubTab: true,
});

const buildAgentSubTabs = (tabs) =>
  tabs
    .filter((tab) => tab.agentSubTab)
    .map((tab) => ({
      name: tab.name,
      subTabs: [],
      icon: tab.icon,
      path: tab.path,
    }));

const buildAgentsTab = (subTabs, userDashboard = []) => ({
  name: "Agents",
  subTabs,
  icon: "fa-table-columns",
  path: "/dashboard",
  component:
    userDashboard.length > 0
      ? isMobile
        ? <ProductDashboardMobile subTabs={userDashboard} />
        : <ProductDashboard subTabs={userDashboard} />
      : <ProductDashboardNoSubscription />,
});

const buildHomeTab = () => ({
  name: "Home",
  subTabs: [],
  icon: "fa-house",
  roles: ["USER", "ADMIN"],
  path: "/home",
  component: isMobile ? <HomeMobile /> : <Home />,
  subscription_type: null,
});

export const createTabs = async (setTabs, setTabsLoaded) => {
  const userRole = getUserRole();
  const subscriptionType = getSubscriptionsType();
  const subscriptionTypes = getSubscriptionTypes();
  const tabConfig = [
    buildAgentRouteTab({
      name: "CoTrader",
      icon: "fa-right-left",
      path: "/co-trader",
      roles: ["USER", "ADMIN"],
      subscription_matcher: (activeSubscriptionTypes) =>
        activeSubscriptionTypes.some((activeSubscriptionType) =>
          subscriptionTypeContains(activeSubscriptionType, "cotrader") ||
          subscriptionTypeContains(activeSubscriptionType, "globallink"),
        ),
      component: isMobile ? <CoTraderCombinedMobile /> : <CoTraderCombined />,
    }),
    buildAgentRouteTab({
      name: "Trade Analysis",
      icon: "fa-magnifying-glass-chart",
      path: "/trade-analysis",
      roles: ["USER", "ADMIN"],
      subscription_type: "TRADEANALYSIS",
      component: <TradeAnalysis />,
    }),
    buildAgentRouteTab({
      name: "CoHedger",
      icon: "fa-shield-halved",
      path: "/co-hedger",
      roles: ["USER", "ADMIN"],
      subscription_type: "COHEDGER",
      component: <CoHedgerSummaryDashboardTile />,
    }),
    {
      name: "Economic Calendar",
      subTabs: [],
      icon: "fa-calendar-days",
      roles: ["USER", "ADMIN"],
      path: "/economic-calendar",
      component: isMobile? <TradingEconomicsCalendarMobile /> : <TradingEconomicsCalendar />,
      subscription_type: "EMALPHA",
    },
    {
      name: "Admin Panel",
      subTabs: [],
      icon: "fa-sliders",
      roles: ["ADMIN"],
      path: "/admin",
      component: <AdminPanel />,
      subscription_type: null,
    },
  ];
  const tabs = tabConfig.filter((tab) => {
    const roleMatch = tab.roles.includes(userRole);
    const allowedSubscriptionTypes =
      tab.subscription_types ||
      (tab.subscription_type ? [tab.subscription_type] : null);
    const subscriptionTypeMatch =
      typeof tab.subscription_matcher === "function"
        ? tab.subscription_matcher(subscriptionTypes)
        : allowedSubscriptionTypes === null ||
          allowedSubscriptionTypes.some((allowedSubscriptionType) =>
            subscriptionTypes.includes(allowedSubscriptionType),
          );
    return roleMatch && subscriptionTypeMatch;
  });

  if (
    String(subscriptionType || "").includes("EMALPHA") ||
    String(subscriptionType || "").includes("INTERNAL")
  ) {
    await FetchUserDashboard(setTabs, tabs, setTabsLoaded);
  } else {
    const agentSubTabs = buildAgentSubTabs(tabs);
    const visibleTabs = tabs.filter((tab) => !tab.hidden);
    const hiddenTabs = tabs.filter((tab) => tab.hidden);
    const nextTabs = [
      buildHomeTab(),
      ...(agentSubTabs.length !== 0 ? [buildAgentsTab(agentSubTabs)] : []),
      ...visibleTabs,
      ...hiddenTabs,
    ];

    setTabs(nextTabs);
    setTabsLoaded(true);
  }
};

// One /auth/get_user_dashboard row as an Agents sub-tab config (ProductDashboard
// subTabs). Shared with partner embeds of a single monitor.
export const toDashboardSubTab = (x) => {
  const name = x.dashboard_name;
  const dashboardName = x.data_reference;
  const s3_bucket_name = x.s3_bucket_name;
  const s3_prefix = x.s3_prefix;
  const start_date_limit =
    x.details_object !== null && x.details_object["start_date_limit"];
  const end_date_limit =
    x.details_object !== null && x.details_object["end_date_limit"];
  const default_start_date =
    x.details_object !== null && x.details_object["default_start_date"];
  const default_end_date =
    x.details_object !== null && x.details_object["default_end_date"];
  const default_country_filter =
    x.details_object !== null && x.details_object["default_country"];
  const default_embedding =
    x.details_object !== null && x.details_object["default_embedding"];
  const default_granularity =
    x.details_object !== null && x.details_object["default_granularity"];
  const available_granularities =
    x.details_object !== null &&
    x.details_object["available_granularities"];
  const filter_countries =
    x.filter_countries && x.filter_countries[0] !== ""
      ? x.filter_countries
      : null;
  const filter_companies =
    x.filter_companies && x.filter_companies[0] !== ""
      ? x.filter_companies
      : null;
  const ner_relevance_cutoff =
    x.details_object !== null && x.details_object["ner_relevance_cutoff"];
  const dashboard_description =
    x.details_object !== null && x.details_object["dashboard_description"];
  const enable_price_overlay =
    x.details_object !== null && x.details_object["enable_price_overlay"];
  const pointClickNewsLookbackDaysRaw =
    x.details_object !== null &&
    x.details_object["point_click_news_lookback_days"];
  const pointClickNewsLookbackDays = Number(pointClickNewsLookbackDaysRaw);
  const parsedNerRelevanceCutoff = Number(ner_relevance_cutoff);
  return decorateDashboardRouteMeta({
    name: name,
    dashboardName: dashboardName,
    s3_bucket_name: s3_bucket_name,
    s3_prefix: s3_prefix,
    icon: "fa-chart-column",
    default_start_date: default_start_date,
    default_end_date: default_end_date,
    filter_countries: filter_countries,
    filter_companies: filter_companies,
    default_embedding: default_embedding,
    default_country_filter: default_country_filter,
    start_date_limit: start_date_limit,
    end_date_limit: end_date_limit,
    ner_relevance_cutoff: Number.isFinite(parsedNerRelevanceCutoff)
      ? parsedNerRelevanceCutoff
      : 0.71,
    dashboard_description:
      typeof dashboard_description === "string"
        ? dashboard_description.trim()
        : "",
    enable_price_overlay: enable_price_overlay === true,
    point_click_news_lookback_days:
      Number.isFinite(pointClickNewsLookbackDays) &&
      pointClickNewsLookbackDays >= 0
        ? pointClickNewsLookbackDays
        : null,
    default_granularity: default_granularity || "DAILY",
    available_granularities: available_granularities || [
      default_granularity || "DAILY",
    ],
  });
};

export const FetchUserDashboard = async (setTabs, tabs, setTabsLoaded) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(
      `${apiUrl}/auth/get_user_dashboard/?user_id=${getUserId()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    const data = await response.json();
    const user_dashboard = data.results.dashboard_details.map(toDashboardSubTab);
    const agentRouteSubTabs = buildAgentSubTabs(tabs);
    const agentSubTabs = [...agentRouteSubTabs, ...user_dashboard];
    const visibleTabs = tabs.filter((tab) => !tab.hidden);
    const hiddenTabs = tabs.filter((tab) => tab.hidden);

    return setTabs(() => [
      buildHomeTab(),
      ...(agentSubTabs.length !== 0
        ? [buildAgentsTab(agentSubTabs, user_dashboard)]
        : []),
      ...visibleTabs,
      ...hiddenTabs,
    ]);
  } catch (error) {
    console.log(error);
  } finally {
    setTabsLoaded(true);
  }
};
